import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/currency-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useStableAuth } from "@/hooks/useStableAuth";
import { useProductTypes } from "@/hooks/useProductTypes";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { resolveMeasurementUnitId } from "@/lib/unitResolve";
import { isSkuDuplicateError, skuErrorMessage } from "@/lib/productSku";
import { createProductsWithDefaultPrice } from "@/lib/defaultPriceTable";
import { isValidGtin, GTIN_INVALID_MESSAGE } from "@/lib/gtin";

/** F2/T2: unidade do vocabulário ativo do tenant exibida no Select */
interface UnitOption {
  id: string;
  name: string;
  sefaz_code: string | null;
}


/** PC-1 (D-PC.1b): markup default herdado (30%), agora só como SUGESTÃO visível no campo. */
const MARKUP_SUGERIDO = 1.3;
const precoSugerido = (custo: number): number =>
  custo > 0 ? Math.round(custo * MARKUP_SUGERIDO * 100) / 100 : 0;

/**
 * Produto devolvido pelo dialog após a criação — obtido por refetch por id com
 * company_id (E-F1.1), nunca pela lista paginada do consumidor. Campos além de
 * id/name/sku são opcionais para manter os consumidores existentes compilando.
 */
export interface QuickCreatedProduct {
  id: string;
  name: string;
  sku?: string;
  ean?: string | null;
  cost_price?: number | null;
  average_cost?: number | null;
  product_type?: string | null;
}

interface QuickProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductCreated: (product: QuickCreatedProduct) => void;
  defaultValues?: {
    name?: string;
    sku?: string;
    ncm?: string;
    ean?: string;
    unit?: string;
    unitPrice?: number;
    /** A-sext.2/F2: conversão declarada no item da nota (ex.: CX × 12). Opcionais — sem eles, 1:1. */
    conversionFactor?: number;
    conversionOperation?: 'multiply' | 'divide';
  };
  /** I.151 1b3-b: não ensina apelido de unidade (assistentes de nota) */
  suppressUnitAlias?: boolean;
}

export function QuickProductDialog({
  open,
  onOpenChange,
  onProductCreated,
  defaultValues,
  suppressUnitAlias = false,
}: QuickProductDialogProps) {
  const { companyId } = useStableAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const [name, setName] = useState(defaultValues?.name || "");
  const [sku, setSku] = useState(defaultValues?.sku || "");
  const [ncm, setNcm] = useState(defaultValues?.ncm || "");
  const [ean, setEan] = useState(defaultValues?.ean || "");
  // F2/T2: a unidade é escolhida por ID no vocabulário ativo do tenant. defaultValues.unit
  // segue sendo o uCom CRU da nota (contrato dos chamadores) — só alimenta a pré-seleção.
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitId, setUnitId] = useState("");
  // I.152: unidade da nota já reconhecida (sigla ou apelido) — base do aviso de unidade diferente
  const [notaUnitId, setNotaUnitId] = useState<string | null>(null);
  // A-sext.2/F2: o custo do produto é por unidade de ESTOQUE. unitPrice chega por unidade da NOTA
  // (vUnCom da caixa); com conversão declarada, × divide o preço e ÷ multiplica — mesma aritmética
  // do payload da compra. 2 casas: é sugestão editável. Sem conversão = unitPrice cru (como antes).
  const conversionFactor =
    defaultValues?.conversionFactor && defaultValues.conversionFactor > 0 ? defaultValues.conversionFactor : 1;
  const conversionOperation = defaultValues?.conversionOperation || 'multiply';
  const custoPrefill = (() => {
    const base = defaultValues?.unitPrice || 0;
    if (conversionFactor === 1) return base;
    const porUnidade = conversionOperation === 'multiply' ? base / conversionFactor : base * conversionFactor;
    return Math.round(porUnidade * 100) / 100;
  })();
  const [costPrice, setCostPrice] = useState(custoPrefill);
  // PC-1 (D-PC.1b): o markup default deixou de ser silencioso. O campo "Preço de Venda" abre
  // PREENCHIDO com a sugestão (custo × MARKUP_SUGERIDO) e o INSERT grava o que está no campo.
  // A sugestão acompanha o custo enquanto o operador não tocar no preço; preço editado à mão
  // desliga o recálculo até a próxima abertura.
  const [sellingPrice, setSellingPrice] = useState(() => precoSugerido(custoPrefill));
  const [priceTouched, setPriceTouched] = useState(false);

  const handleCostChange = (valor: number) => {
    setCostPrice(valor);
    if (!priceTouched) setSellingPrice(precoSugerido(valor));
  };

  const handleSellingPriceChange = (valor: number) => {
    setPriceTouched(true);
    setSellingPrice(valor);
  };

  /** Custo novo vindo do prefill (abertura): sugestão refeita e recálculo religado. */
  const aplicarCustoInicial = (custo: number) => {
    setCostPrice(custo);
    setSellingPrice(precoSugerido(custo));
    setPriceTouched(false);
  };
  // C-1 (D-C.14): o tipo vem do vocabulário do tenant (product_types), mesma fonte do
  // UnifiedProductDialog. Só tipos COM internal_code: o value gravado em products.product_type
  // é o código que os leitores entendem ('gas' liga a chama e a conversão na compra). O
  // fallback pelo nome está morto — foi a porta do intruso 'Produto Padrão'.
  const { productTypes, isLoading: isLoadingTypes } = useProductTypes();
  const typeOptions = productTypes.filter((t) => (t.internal_code ?? "").trim() !== "");
  const defaultTypeCode =
    typeOptions.find((t) => t.internal_code === "product")?.internal_code ??
    typeOptions[0]?.internal_code ??
    "";
  const [productType, setProductType] = useState<string>("");

  // Default visível e válido: aplica quando as opções chegam ou quando o valor atual não é opção
  useEffect(() => {
    if (!defaultTypeCode) return;
    setProductType((atual) =>
      typeOptions.some((t) => t.internal_code === atual) ? atual : defaultTypeCode,
    );
    // typeOptions é derivado de productTypes (nova referência a cada render) — deps primitivas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTypeCode, productTypes.length]);

  // A cada abertura volta ao default: a escolha anterior não pode grudar no próximo produto
  useEffect(() => {
    if (open && defaultTypeCode) setProductType(defaultTypeCode);
  }, [open, defaultTypeCode]);

  // F1 / cura (a): quando o dialog abre por ESTADO do pai (open=true), o Radix não chama
  // onOpenChange — o prefill de handleOpenChange nunca disparava. Sincroniza aqui, com deps
  // primitivas (o pai passa um objeto literal novo a cada render).
  useEffect(() => {
    if (!open) return;
    setName(defaultValues?.name ?? "");
    setSku(defaultValues?.sku ?? "");
    setNcm(defaultValues?.ncm ?? "");
    setEan(defaultValues?.ean ?? "");
    aplicarCustoInicial(custoPrefill);
  }, [
    open,
    defaultValues?.name,
    defaultValues?.sku,
    defaultValues?.ncm,
    defaultValues?.ean,
    defaultValues?.unitPrice,
    defaultValues?.conversionFactor,
    defaultValues?.conversionOperation,
  ]);

  // F2/T2: vocabulário ATIVO do tenant
  const loadUnits = async (): Promise<UnitOption[]> => {
    if (!companyId) return [];
    const { data } = await supabase
      .from("measurement_units")
      .select("id, name, sefaz_code")
      .eq("company_id", companyId)
      .eq("active", true)
      .order("name");
    const list = (data || []) as UnitOption[];
    setUnits(list);
    return list;
  };

  // F2/T1: resolvedor único (src/lib/unitResolve.ts — I.151 1b3-b, compartilhado com os assistentes)
  const resolveUnitId = (rawSigla: string): Promise<string | null> =>
    resolveMeasurementUnitId(companyId, rawSigla);

  // Ao abrir: carrega o vocabulário e pré-seleciona a resolução automática do uCom;
  // sem match → "" (placeholder "Selecionar" — o operador escolhe; unidade nova só em Configurações)
  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    (async () => {
      const list = await loadUnits();
      const resolved = await resolveUnitId(defaultValues?.unit ?? "UN");
      if (!cancelled) {
        setUnitId(resolved && list.some((u) => u.id === resolved) ? resolved : "");
        setNotaUnitId(defaultValues?.unit ? resolved : null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, defaultValues?.unit]);

  // Reset form when dialog opens with new defaults
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && isCreating) return;
    if (isOpen && defaultValues) {
      setName(defaultValues.name || "");
      setSku(defaultValues.sku || "");
      setNcm(defaultValues.ncm || "");
      setEan(defaultValues.ean || "");
      aplicarCustoInicial(custoPrefill);
    }
    onOpenChange(isOpen);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error("Nome do produto é obrigatório");
      return;
    }

    if (!companyId) {
      toast.error("Empresa não identificada");
      return;
    }

    // D-F2.17: unidade obrigatória — produto sem unidade não nasce mais por esta rota
    if (!unitId) {
      toast.error("Selecione a unidade de medida");
      return;
    }

    // C-1: tipo obrigatório e DENTRO do vocabulário do tenant
    if (!typeOptions.some((t) => t.internal_code === productType)) {
      toast.error("Selecione o tipo de produto");
      return;
    }

    // LARI-GTIN-INTERNO (I.178): o GTIN (inclusive o cEAN do XML) só entra com dígito certo — a mesma régua da trava do
    // banco; vazio segue aceito
    if (ean.trim() && !isValidGtin(ean.trim())) {
      toast.error(GTIN_INVALID_MESSAGE);
      return;
    }

    setIsCreating(true);
    try {
      // PRECO-PADRAO (I.160, resposta 1): produto e linha da "Preço Padrão" na mesma transação, pela função do banco;
      // o preço de venda vai para a linha e o cadastro espelha
      const [newId] = await createProductsWithDefaultPrice([{
        name: name.trim(),
        sku: sku.trim() || null,
        ncm: ncm.trim() || null,
        ean: ean.trim() || null,
        measurement_unit_id: unitId,
        cost_price: costPrice,
        sale_price: sellingPrice, // PC-1: grava o que está no campo — sem fallback silencioso
        product_type: productType,
        active: true,
      }]);
      const data = { id: newId };

      // F2/T3: de-para aprendiz — quando o operador ensina a unidade de um uCom que o sistema
      // NÃO resolvia, grava o alias em unit_aliases (que o resolveUnitId já consulta) e nunca
      // mais pergunta. Best-effort, silencioso, IIFE destacada: NUNCA bloqueia nem atrasa a
      // criação do produto. E-T3.1: só aprende uCom não-resolvido (jaResolve !== null sai) —
      // sob a precedência sefaz_code-primeiro, "corrigir" um mapeamento existente via alias é
      // impossível por construção (linha morta ou 23505); isso se edita direto em unit_aliases.
      void (async () => {
        try {
          // A-sext.2/F2: com conversão declarada a unidade escolhida difere da da nota POR DESENHO
          // (CX × 12 → UN) — isso não é apelido; ensinar CX→UN envenenaria toda nota futura com CX.
          if (conversionFactor !== 1) return;
          // I.151 1b3-b: nos assistentes de nota a embalagem vira unidade alternativa do produto,
          // nunca apelido — apelido só pelas Configurações (decisão do Diretor, 10/10)
          if (suppressUnitAlias) return;
          const sigla = (defaultValues?.unit ?? "").trim().toUpperCase();
          if (!sigla || sigla.length > 10 || !unitId) return;
          const escolhida = units.find((u) => u.id === unitId);
          if ((escolhida?.sefaz_code ?? "").trim().toUpperCase() === sigla) return;
          const jaResolve = await resolveUnitId(sigla);
          if (jaResolve !== null) return;
          const { error: aliasError } = await (supabase as any)
            .from("unit_aliases")
            .insert({ company_id: companyId, alias: sigla, measurement_unit_id: unitId });
          // 23505 = UNIQUE (company_id, alias): outro operador ensinou antes — não sobrescrever
          if (aliasError && aliasError.code !== "23505") {
            console.warn("[QuickProductDialog] aprendiz de unidade:", aliasError.message);
          }
        } catch (err) {
          console.warn("[QuickProductDialog] aprendiz de unidade (exceção):", err);
        }
      })();

      // F1 / cura (b) + E-F1.1: o consumidor NÃO procura o recém-criado na lista paginada
      // (products.find falhava: a lista ainda não o contém) — refetch por id com o tenant
      // explícito e devolução do produto completo no callback.
      // PROD-CUSTO-RLS 15a: relê sem as colunas de custo; o custo devolvido é o informado neste cadastro
      const { data: created, error: refetchError } = await supabase
        .from("products")
        .select("id, name, sku, ean, product_type")
        .eq("id", data.id)
        .eq("company_id", companyId)
        .single();

      if (refetchError || !created) {
        throw refetchError ?? new Error("Produto criado, mas não foi possível recarregá-lo");
      }

      toast.success(`Produto "${name}" criado com sucesso!`);
      // BUG-C23: Invalidate product cache
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['unified-products'] });
      queryClient.invalidateQueries({ queryKey: ['product-fuzzy'] });
      onProductCreated({
        id: created.id,
        name: created.name,
        sku: created.sku || undefined,
        ean: created.ean,
        cost_price: costPrice,
        average_cost: null, // produto recém-criado: o custo médio nasce do primeiro movimento
        product_type: created.product_type,
      });

      // Reset form
      setName("");
      setSku("");
      setNcm("");
      setEan("");
      setUnitId("");
      aplicarCustoInicial(0);
      onOpenChange(false);
    } catch (error: any) {
      console.error("Erro ao criar produto:", error);
      toast.error("Erro ao criar produto", {
        // GRADE-2 + PROD-SKU-UNICO: recusa de SKU (função de criação ou índice) como "SKU já cadastrado: X"
        description: isSkuDuplicateError(error) ? skuErrorMessage(error, sku) : error.message,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => { if (isCreating) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isCreating) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Criar Novo Produto</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome do Produto *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do produto"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sku">SKU / Código</Label>
              <Input
                id="sku"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="Código interno"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ean">EAN / GTIN</Label>
              <Input
                id="ean"
                value={ean}
                onChange={(e) => setEan(e.target.value)}
                placeholder="Código de barras"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ncm">NCM</Label>
              <Input
                id="ncm"
                value={ncm}
                onChange={(e) => setNcm(e.target.value)}
                placeholder="00000000"
                maxLength={8}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">Unidade *</Label>
              <div className="flex gap-2">
                <Select value={unitId} onValueChange={setUnitId}>
                  <SelectTrigger id="unit" className="flex-1">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}{u.sefaz_code ? ` (${u.sefaz_code})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* I.152: unidade nova só em Configurações › Unidades (regra do Diretor); aqui só as existentes */}
              <p className="text-xs text-muted-foreground">
                Falta a unidade? Cadastre em Configurações › Unidades de medida.
              </p>
              {/* I.152: a unidade da nota diferente da escolhida não é convertida aqui — o passo seguinte pede
                  confirmação ("É a base mesmo") ou o cadastro da unidade do produto */}
              {defaultValues?.unit && (
                <p className="text-xs text-muted-foreground">
                  Unidade da nota: {defaultValues.unit}
                  {unitId && unitId !== notaUnitId
                    ? ` · o produto será criado em ${units.find((u) => u.id === unitId)?.sefaz_code || 'outra unidade'}; no passo seguinte o sistema pede para confirmar ou cadastrar a unidade`
                    : ''}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="productType">Tipo de Produto</Label>
            <Select
              value={productType}
              // Guarda Radix: nunca aceitar onValueChange vazio
              onValueChange={(v) => { if (v) setProductType(v); }}
              disabled={isLoadingTypes}
            >
              <SelectTrigger id="productType">
                <SelectValue placeholder={isLoadingTypes ? "Carregando tipos..." : "Selecione o tipo"} />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((type) => (
                  <SelectItem key={type.id} value={type.internal_code as string}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="costPrice">Preço de Custo</Label>
              <CurrencyInput
                value={costPrice}
                onChange={handleCostChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sellingPrice">Preço de Venda</Label>
              <CurrencyInput
                value={sellingPrice}
                onChange={handleSellingPriceChange}
              />
              {!priceTouched && sellingPrice > 0 && (
                <p className="text-xs text-muted-foreground">
                  Sugestão: custo + {Math.round((MARKUP_SUGERIDO - 1) * 100)}%. Edite para definir outro preço.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Criar Produto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    </>
  );
}
